import {
  AddToCalendarAction,
  AuthenticatedEvent,
  DownloadDocumentAction,
  ExitAction,
  OpenExternalUrlAction,
  SessionExpiredEvent,
} from './messages'
import type {
  AddToCalendarDelegate,
  AuthTokenDelegate,
  CreateExternalUrlHandleOptions,
  DocumentDownloadDelegate,
  ExternalUrlDelegate,
  ExternalUrlOpenHandle,
  LifecycleDelegate,
  PlatformDelegate,
} from './types'

type FlutterCallHandler = (
  handlerName: string,
  ...args: unknown[]
) => Promise<unknown>

interface NativeWindow extends Window {
  ReactNativeWebView?: {
    postMessage(message: string): void
  }
  flutter_inappwebview?: {
    callHandler?: FlutterCallHandler
  }
  webkit?: {
    messageHandlers?: {
      nativeApp?: {
        postMessage(data: unknown): void
      }
    }
  }
  Android?: {
    postMessage(message: string): void
  }
}

type NativeMessage = object

class NativeExternalUrlDelegate implements ExternalUrlDelegate {
  createHandle(
    _options?: CreateExternalUrlHandleOptions,
  ): ExternalUrlOpenHandle {
    return {
      open(url: string) {
        sendNativeMessage(OpenExternalUrlAction(url))
      },
      abort() {
        // no-op
      },
    }
  }
}

class NativeDocumentDownloadDelegate implements DocumentDownloadDelegate {
  download(url: string): void {
    sendNativeMessage(DownloadDocumentAction(url))
  }
}

class NativeAddToCalendarDelegate implements AddToCalendarDelegate {
  addToCalendar(icsFile: File): void {
    const reader = new FileReader()
    reader.onloadend = () =>
      sendNativeMessage(AddToCalendarAction(reader.result as string))
    reader.readAsDataURL(icsFile)
  }
}

class NativeLifecycleDelegate implements LifecycleDelegate {
  readonly supportsDelegatedExit = true

  notifyAuthenticated(): void {
    sendNativeMessage(AuthenticatedEvent())
  }

  notifySessionExpired(): void {
    sendNativeMessage(SessionExpiredEvent())
  }

  requestExit(_returnUrl: string): void {
    sendNativeMessage(ExitAction())
  }
}

class NativeAuthTokenDelegate implements AuthTokenDelegate {
  async getToken(): Promise<string | null> {
    const callHandler = (window as NativeWindow).flutter_inappwebview
      ?.callHandler
    if (typeof callHandler !== 'function') {
      return null
    }

    const token = await callHandler('getAuthToken')
    return typeof token === 'string' && token.length > 0 ? token : null
  }
}

export class NativePlatformDelegate implements PlatformDelegate {
  private readonly externalUrlDelegate = new NativeExternalUrlDelegate()
  private readonly documentDownloadDelegate =
    new NativeDocumentDownloadDelegate()

  private readonly addToCalendarDelegate = new NativeAddToCalendarDelegate()
  private readonly lifecycleDelegate = new NativeLifecycleDelegate()
  private readonly authTokenDelegate = new NativeAuthTokenDelegate()

  getExternalUrlDelegate = () => this.externalUrlDelegate
  getDocumentDownloadDelegate = () => this.documentDownloadDelegate
  getAddToCalendarDelegate = () => this.addToCalendarDelegate
  getLifecycleDelegate = () => this.lifecycleDelegate
  getAuthTokenDelegate = () => this.authTokenDelegate

  static get isSupported() {
    return isEmbeddedInNative()
  }
}

function isEmbeddedInNative() {
  const nativeWindow = window as NativeWindow
  return Boolean(
    nativeWindow.ReactNativeWebView ||
      nativeWindow.flutter_inappwebview ||
      nativeWindow.webkit?.messageHandlers?.nativeApp ||
      nativeWindow.Android,
  )
}

function sendNativeMessage(data: NativeMessage) {
  const nativeWindow = window as NativeWindow
  const message = JSON.stringify(data)

  if (nativeWindow.ReactNativeWebView) {
    nativeWindow.ReactNativeWebView.postMessage(message)
    return
  }

  const callHandler = nativeWindow.flutter_inappwebview?.callHandler
  if (typeof callHandler === 'function') {
    callHandler('taxfyleMessageHandler', message)
    return
  }

  if (nativeWindow.webkit?.messageHandlers?.nativeApp) {
    nativeWindow.webkit.messageHandlers.nativeApp.postMessage(data)
    return
  }

  if (nativeWindow.Android?.postMessage) {
    nativeWindow.Android.postMessage(message)
    return
  }

  window.parent.postMessage(data, '*')
}
